import type { FreeAllowanceRefusalResponse, TenantUsageSummaryResponse } from '@contract-analyzer/api-client';
import { ArrowRightIcon } from 'lucide-react';
import { NavLink } from 'react-router';

import { canAccessWorkspaceBilling, canSubmitChargeCodes } from '@/components/charge-codes/charge-code-utils';
import { PIP_DISPLAY_LIMIT, QuotaRunway } from '@/components/quota-runway';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { QuotaResource } from '@/hooks/quota-block-context';
import { useAuth } from '@/hooks/use-auth';
import { useExtractionRunway } from '@/hooks/use-extraction-runway';
import { useNow } from '@/hooks/use-now';
import { useTenant } from '@/hooks/use-tenant';
import { billingSettingsPath } from '@/lib/routes';
import { formatCount } from '@/lib/format/count';
import { formatTrialDate, type TrialPhase } from '@/lib/trial-phase';

interface QuotaBlockDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly resourceName: QuotaResource;
  /** Where the free allowance stood when it refused, as the 402 said; `null` when it did not say. */
  readonly allowance: FreeAllowanceRefusalResponse | null;
  readonly now?: Date;
}

/** One allowance's numbers, which the title, the description and the pips all read. */
interface ResourceFacts {
  readonly quota: number;
  readonly used: number;
  /** What the refused request asked for; `null` when only the usage summary is known. */
  readonly requested: number | null;
}

const SINGULAR: Readonly<Record<QuotaResource, string>> = {
  documents: 'document',
  'analysis runs': 'analysis run',
  extractions: 'extraction',
};

/**
 * The refusal's own numbers, or the workspace's usage when the refusal carried
 * none (a refusal of an exhausted allowance reads the same either way). A zero
 * allowance has nothing to count, so the dialog names the allowance instead.
 */
function resolveResourceFacts(
  resourceName: QuotaResource,
  allowance: FreeAllowanceRefusalResponse | null,
  usage: TenantUsageSummaryResponse | null,
): ResourceFacts | null {
  if (allowance !== null) {
    return allowance.limit > 0
      ? { quota: allowance.limit, used: Math.min(allowance.used, allowance.limit), requested: allowance.requested }
      : null;
  }
  if (usage === null) return null;
  if (resourceName === 'documents' && usage.document_quota > 0) {
    return { quota: usage.document_quota, used: usage.documents_uploaded, requested: null };
  }
  if (resourceName === 'analysis runs' && usage.analysis_run_quota > 0) {
    return { quota: usage.analysis_run_quota, used: usage.analysis_runs_started, requested: null };
  }
  if (resourceName === 'extractions' && usage.extraction_quota > 0) {
    return { quota: usage.extraction_quota, used: usage.extraction_quota_used, requested: null };
  }
  return null;
}

function noun(value: number, resourceName: QuotaResource): string {
  return value === 1 ? SINGULAR[resourceName] : resourceName;
}

function countOf(value: number, resourceName: QuotaResource): string {
  return `${formatCount(value)} free ${noun(value, resourceName)}`;
}

function isOrAre(value: number): string {
  return value === 1 ? 'is' : 'are';
}

function remaining(facts: ResourceFacts): number {
  return Math.max(facts.quota - facts.used, 0);
}

function dialogTitle(
  resourceName: QuotaResource,
  facts: ResourceFacts | null,
  trial: TrialPhase,
  now: Date,
): string {
  // A trial that just ended is the reason the allowance applies at all; lead
  // with that so the number below is explained rather than surprising.
  if (trial.kind === 'ended') {
    return `Your trial ended on ${formatTrialDate(trial.endedAt, now)}`;
  }
  if (facts === null) return `You've used the free ${resourceName} allowance`;
  const left = remaining(facts);
  if (left === 0) return `You've used your ${countOf(facts.quota, resourceName)}`;
  // Refused for asking more than is left, not for running out.
  return `Only ${formatCount(left)} of your ${countOf(facts.quota, resourceName)} ${isOrAre(left)} left`;
}

/** What the title cannot say: the allowance a trial left behind, and what the request needed. */
function shortfall(resourceName: QuotaResource, facts: ResourceFacts | null, trial: TrialPhase): string {
  if (facts === null) return '';
  const left = remaining(facts);
  const needs = left > 0 && facts.requested !== null ? `This needs ${formatCount(facts.requested)}. ` : '';
  if (trial.kind !== 'ended') return needs;
  const applies = `The free allowance of ${formatCount(facts.quota)} ${noun(facts.quota, resourceName)} now applies`;
  return left === 0 ? `${applies} and is used up. ` : `${applies}, and ${formatCount(left)} ${isOrAre(left)} left. ${needs}`;
}

/**
 * The 402 moment, framed as a completed trial rather than a failure: what
 * the user made stays available, and the way forward is one action wide.
 * Charge-code-aware — a pending request reassures instead of selling.
 */
export function QuotaBlockDialog({
  open,
  onOpenChange,
  resourceName,
  allowance,
  now: fixedNow,
}: QuotaBlockDialogProps) {
  const now = useNow(fixedNow);
  const tenant = useTenant();
  const { user } = useAuth();
  const state = useExtractionRunway({ enabled: open });
  const facts = resolveResourceFacts(resourceName, allowance, state.status === 'ready' ? state.usage : null);
  const chargeCodeReadiness = state.status === 'ready' ? state.chargeCodeReadiness : 'none';
  const trial: TrialPhase = state.status === 'ready' ? state.trial : { kind: 'none' };
  const billingPath = billingSettingsPath(tenant.slug);
  const canSubmitChargeCode = canSubmitChargeCodes(user);
  const canAccessBilling = canAccessWorkspaceBilling(user);

  const actionLabel =
    chargeCodeReadiness === 'pending'
      ? 'View request'
      : !canSubmitChargeCode
        ? 'View billing'
        : chargeCodeReadiness === 'rejected'
          ? 'Submit a different code'
          : 'Add charge code';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-bold tracking-tight">
            {dialogTitle(resourceName, facts, trial, now)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {shortfall(resourceName, facts, trial)}
            Nothing is lost — every result and export stays available, and new work resumes
            as soon as billing is set up.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {facts !== null && facts.quota <= PIP_DISPLAY_LIMIT ? (
          <QuotaRunway noun={`free ${resourceName}`} quota={facts.quota} used={facts.used} />
        ) : null}

        {chargeCodeReadiness === 'pending' ? (
          <div className="rounded-lg border bg-muted/40 p-4 text-sm font-light">
            <span className="font-medium">Charge code pending approval.</span> Work resumes
            automatically once it clears — nothing else to do.
          </div>
        ) : null}

        {!canAccessBilling ? (
          <p className="text-sm text-muted-foreground">Ask a workspace admin to set up billing.</p>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel className="h-11 px-4">View my results</AlertDialogCancel>
          {canAccessBilling ? (
            <Button asChild onClick={() => { onOpenChange(false); }} className="h-11">
              <NavLink to={billingPath}>
                {actionLabel}
                <ArrowRightIcon data-icon="inline-end" />
              </NavLink>
            </Button>
          ) : null}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
